/**
 * The Real Cashback Ledger (§9, §13.3) — the page the app is named for.
 *
 * The design serves one idea: **the headline number means exactly one thing.**
 * Real cashback is money that actually reduced a bill. So:
 *
 *   * Realized rewards — value that landed somewhere other than the bill — is a
 *     second, visually quieter figure. Adding the two together would produce a
 *     bigger, less true number.
 *   * Credits no rule explains are shown, counted, and *excluded*. They are the
 *     honest middle: not cashback until the user says so, but not swept away
 *     either. Without this, a gap in the rules looks like an absence of money.
 *   * A card with no matched cashback shows its rate with a caveat rather than
 *     a confident 0.00%, when it has unexplained credits sitting there.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Coins,
  Download,
  HelpCircle,
  Plus,
  Sparkles,
  Trash2,
  Undo2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { CardFan, type FanItem } from "@/components/cards/CardFan";
import { Benefits } from "@/components/cashback/Benefits";
import { MonthlyBreakdown } from "@/components/cashback/MonthlyBreakdown";
import {
  Badge,
  Button,
  CardSwatch,
  EmptyState,
  Field,
  Input,
  Panel, PageHeader, Segmented, SELECT_CLASS } from "@/components/ui/primitives";
import { Odometer } from "@/components/ui/motion";
import {
  cardsApi,
  cashbackApi,
  type Benefits as BenefitsData,
  type CardBreakdown,
  type CashbackEntry,
  type LedgerSummary,
  type UnexplainedCredit,
} from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatFiscalYear, formatMoney } from "@/lib/format";

export function CashbackPage() {
  const [fy, setFy] = useState<string | undefined>();
  const [adding, setAdding] = useState(false);
  const [showUnexplained, setShowUnexplained] = useState(false);
  // One card, or the whole wallet. Every section below reads this.
  const [cardId, setCardId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["cashback", fy],
    queryFn: () => cashbackApi.summary(fy),
  });

  const activeFy = data?.fiscal_year ?? fy;

  const { data: entries } = useQuery({
    queryKey: ["cashback", "entries", activeFy],
    queryFn: () => cashbackApi.entries(activeFy),
    enabled: Boolean(activeFy),
  });

  const { data: unexplained } = useQuery({
    queryKey: ["cashback", "unexplained", activeFy],
    queryFn: () => cashbackApi.unexplained(activeFy),
    enabled: Boolean(activeFy) && (showUnexplained || cardId !== null),
  });

  const { data: benefits } = useQuery({
    queryKey: ["cashback", "benefits", activeFy],
    queryFn: () => cashbackApi.benefits(activeFy),
    enabled: Boolean(activeFy),
  });

  if (isError) {
    return (
      <div className="mx-auto max-w-5xl">
        <Panel className="p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-overdue" aria-hidden />
            <div className="flex-1">
              <p className="text-sm text-ink">Couldn't load the cashback ledger.</p>
              <p className="mt-0.5 text-xs text-muted">{(error as Error).message}</p>
            </div>
            <Button variant="default" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="The headline feature"
        title={
          <>
            Real <em className="accent-serif">cashback</em>
          </>
        }
        description="Money your spending actually earned. Not points, not pending offers — only credits that appeared on a statement, net of anything the bank took back."
        actions={
        <>
          {data && data.fiscal_years.length > 1 && (
            <Segmented
              label="Fiscal year"
              value={data.fiscal_year}
              onChange={setFy}
              options={data.fiscal_years.map((year) => ({
                value: year,
                label: formatFiscalYear(year),
              }))}
            />
          )}
          {data && (
            <>
              <Button asChild variant="default" size="sm">
                <a href={cashbackApi.exportUrl(data.fiscal_year, "xlsx")} download>
                  <Download className="h-4 w-4" aria-hidden />
                  Excel
                </a>
              </Button>
              <Button asChild variant="default" size="sm">
                <a href={cashbackApi.exportUrl(data.fiscal_year, "csv")} download>
                  CSV
                </a>
              </Button>
            </>
          )}
        </>
        }
      />

      {isLoading || !data ? (
        <>
          <Panel className="p-6">
            <div className="skeleton h-3 w-32" />
            <div className="skeleton mt-4 h-12 w-64" />
            <div className="skeleton mt-3 h-3 w-48" />
          </Panel>
          <Panel className="space-y-2 p-4">
            <div className="skeleton h-9 w-full" />
            <div className="skeleton h-9 w-full" />
          </Panel>
        </>
      ) : (
        <CardScoped
          data={data}
          cardId={cardId}
          setCardId={setCardId}
          entries={entries}
          unexplained={unexplained}
          benefits={benefits}
          showUnexplained={showUnexplained}
          setShowUnexplained={setShowUnexplained}
          onAdd={() => setAdding(true)}
        />
      )}

      {adding && data && (
        <ManualEntryDialog
          fiscalYear={data.fiscal_year}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  );
}

/**
 * Everything below the header, narrowed to the picked card (or not).
 *
 * The picker is the same fanned hand as the dashboard, with an "All cards"
 * chip. Picking a card re-keys the headline so its figure rolls to the card's
 * own; the month chart, the table, benefits and entries all follow. Exports
 * stay whole-year: a file named for the FY should contain the FY.
 */
function CardScoped({
  data,
  cardId,
  setCardId,
  entries: allEntries,
  unexplained: allUnexplained,
  benefits: allBenefits,
  showUnexplained,
  setShowUnexplained,
  onAdd,
}: {
  data: LedgerSummary;
  cardId: string | null;
  setCardId: (id: string | null) => void;
  entries: CashbackEntry[] | undefined;
  unexplained: UnexplainedCredit[] | undefined;
  benefits: BenefitsData | undefined;
  showUnexplained: boolean;
  setShowUnexplained: (fn: (v: boolean) => boolean) => void;
  onAdd: () => void;
}) {
  const card = data.cards.find((c) => c.card_id === cardId) ?? null;
  const entries = card ? allEntries?.filter((e) => e.card_id === card.card_id) : allEntries;
  const unexplained = card
    ? allUnexplained?.filter((u) => u.card_id === card.card_id)
    : allUnexplained;
  const unexplainedCount = card ? card.unexplained_count : data.unexplained_count;
  const unexplainedTotal = card
    ? unexplained
      ? String(unexplained.reduce((a, u) => a + Number(u.amount), 0))
      : null
    : data.unexplained_total;
  const benefits = allBenefits && card ? benefitsFor(allBenefits, card.card_id) : allBenefits;

  // Least to most, left to right: a fan shows its rightmost card whole, so the
  // top earner is the one on top of the hand.
  const fanItems: FanItem[] = [...data.cards]
    .sort((a, b) => Number(a.real_cashback) - Number(b.real_cashback))
    .map((c) => ({
      id: c.card_id,
      issuer: c.issuer,
      name: c.nickname || c.product_name || c.issuer,
      last4: c.last4,
      theme: c.theme,
      figure: { label: "Cashback", value: formatMoney(c.real_cashback) },
    }));

  return (
        <>
          <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
            <Panel className="animate-rise p-5">
              <div className="flex items-center justify-between">
                <span className="label-micro">
                  {card ? "One card" : `Whole wallet · ${data.cards.length} cards`}
                </span>
                <span className="hidden text-[11px] text-faint sm:inline">Pick a card to narrow the page</span>
              </div>
              <CardFan
                className="mt-3"
                items={fanItems}
                selectedId={cardId}
                onSelect={setCardId}
                allowAll
                maxCardWidth={210}
                label="Show cashback for"
              />
            </Panel>
            <Headline data={data} card={card} />
          </div>

          {/* Unexplained credits — the honesty valve. */}
          {unexplainedCount > 0 && (
            <Panel className="border-warn/25 bg-warn/[0.04] p-4">
              <button
                type="button"
                onClick={() => setShowUnexplained((v) => !v)}
                className="flex w-full items-start gap-3 text-left focus-visible:outline-none"
              >
                <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-warn" aria-hidden />
                <span className="flex-1">
                  <span className="block text-sm text-ink">
                    {unexplainedCount} statement credit
                    {unexplainedCount === 1 ? "" : "s"}
                    {unexplainedTotal !== null && <> worth {formatMoney(unexplainedTotal)}</>} that
                    no rule explains
                  </span>
                  <span className="mt-0.5 block text-xs text-muted">
                    These reduced a bill, but MyDeck doesn't know whether they were cashback,
                    a fee reversal, or a refund — so they are <em>not</em> in the total above.
                    Add a rule in Settings and they'll be counted.
                  </span>
                </span>
                <span className="shrink-0 text-xs text-warn">
                  {showUnexplained ? "Hide" : "Show"}
                </span>
              </button>

              {showUnexplained && unexplained && (
                <ul className="mt-3 divide-y divide-hairline border-t border-hairline">
                  {unexplained.map((u) => (
                    <li key={u.transaction_id} className="flex items-center gap-3 py-2 text-sm">
                      <span className="w-24 shrink-0 tabular-nums text-muted">
                        {formatDate(u.txn_date)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-ink" title={u.description}>
                        {u.description}
                      </span>
                      <span className="shrink-0 tabular-nums text-ink">
                        {formatMoney(u.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          )}

          {/* Month before card: "when did this arrive?" is the question the
              headline provokes first, and the per-card table answers "from
              where?" once the shape of the year is on screen. */}
          <MonthlyBreakdown
            key={card?.card_id ?? "all"}
            fiscalYear={data.fiscal_year}
            cardId={card?.card_id}
            pickerHidden
          />

          <PerCard cards={data.cards} selectedId={cardId} onSelect={setCardId} />

          {benefits && <Benefits data={benefits} />}

          <Entries
            entries={entries}
            fiscalYear={data.fiscal_year}
            cardName={card ? card.nickname || card.product_name || card.issuer : null}
            onAdd={onAdd}
          />
        </>
  );
}

/** The year's benefits narrowed to one card. Zeroes where it has none. */
function benefitsFor(all: BenefitsData, cardId: string): BenefitsData {
  const fuelCard = all.fuel_cards.find((c) => c.card_id === cardId);
  const fee = all.fee_reversal_cards.find((c) => c.card_id === cardId);
  const fuel: BenefitsData["fuel"] = fuelCard ?? {
    ...all.fuel,
    levied: "0",
    waived: "0",
    net_cost: "0",
    levied_count: 0,
    waived_count: 0,
    waived_share: null,
    fuel_spend: "0",
    fuel_txn_count: 0,
    surcharged_fuel_spend: "0",
    observed_rate: null,
    reconciled: null,
    unmatched_surcharges: 0,
    unmatched_surcharge_amount: "0",
  };
  return {
    ...all,
    total: String(Number(fuel.waived) + Number(fee?.amount ?? 0)),
    fuel,
    fuel_cards: fuelCard ? [fuelCard] : [],
    fee_reversals: fee?.amount ?? "0",
    fee_reversal_count: fee?.count ?? 0,
    fee_reversal_cards: fee ? [fee] : [],
  };
}

function Headline({ data, card }: { data: LedgerSummary; card: CardBreakdown | null }) {
  // One shape for both views, so the panel never changes layout under the
  // rolling figure — only the numbers move.
  const v = card ?? data;
  const earningCards = data.cards.filter((c) => Number(c.real_cashback) > 0).length;
  const name = card ? card.nickname || card.product_name || card.issuer : null;

  return (
    <div className="grid gap-4">
      <Panel className="panel-gold animate-rise p-6 sm:p-7">
        <div className="label-micro">
          Real cashback · {formatFiscalYear(data.fiscal_year)}
          {name && <> · {name}</>}
        </div>
        <div className="mt-4 text-hero font-normal text-brass">
          <Odometer
            key={`${data.fiscal_year}-${card?.card_id ?? "all"}`}
            value={formatMoney(v.real_cashback)}
            delay={0.1}
            minorClassName="text-[0.45em] text-brass-dim"
          />
        </div>
        <p className="mt-2 text-sm text-muted">
          {v.entry_count} statement credit{v.entry_count === 1 ? "" : "s"}
          {!card && (
            <>
              {" "}across {earningCards} card{earningCards === 1 ? "" : "s"}
            </>
          )}
          {v.effective_rate && (
            <>
              {" · "}
              <span className="text-ink">{v.effective_rate}%</span> of{" "}
              {formatMoney(v.eligible_spend)} eligible spend
            </>
          )}
        </p>
        {/* Netting has to be visible. A total that silently absorbed a
            clawback would look like a smaller month rather than a reversal,
            and the user would have no way to tell the difference. */}
        {Number(v.clawbacks) > 0 && (
          <p className="mt-2 text-sm text-muted">
            After {formatMoney(v.clawbacks)} the bank took back on reversed purchases.
          </p>
        )}
        {card?.rewards_note && Number(card.real_cashback) === 0 && (
          <p className="mt-2 text-sm text-faint">{card.rewards_note}</p>
        )}
        {card?.credit_timing === "cycle_credit" && (
          <p className="mt-2 text-xs text-faint">
            This card credits a cycle&rsquo;s cashback on the next statement.
          </p>
        )}
        {!card && (
          <p className="mt-3 text-xs text-faint">
            Lifetime: {formatMoney(data.lifetime_real_cashback)}
          </p>
        )}
      </Panel>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Deliberately quieter than the headline: real value, but it never
            reduced a bill, so it is not the number this app is about. */}
        <Panel className="animate-rise p-5" style={{ animationDelay: "60ms" }}>
          <div className="label-micro">Realized rewards</div>
          <div className="mt-3 text-figure font-light text-ink">
            {formatMoney(v.realized_rewards)}
          </div>
          <p className="mt-1 text-xs text-faint">
            Value taken outside the statement — vouchers, wallet credits. Kept out of the
            headline on purpose.
          </p>
        </Panel>

        <Panel className="animate-rise p-5" style={{ animationDelay: "120ms" }}>
          <div className="label-micro">Fees &amp; interest paid</div>
          <div className="mt-3 text-figure font-light text-ink">
            {formatMoney(String(Number(v.fees) + Number(v.interest)))}
          </div>
          <p className="mt-1 text-xs text-faint">
            {formatMoney(v.fees)} fees · {formatMoney(v.interest)} interest
          </p>
        </Panel>
      </div>
    </div>
  );
}

function PerCard({
  cards,
  selectedId,
  onSelect,
}: {
  cards: CardBreakdown[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  // Any card you actually spent on belongs here, including the ones that earned
  // nothing. Hiding those would answer "which cards pay?" while quietly
  // dropping the answer to "which cards don't?" — the more useful question.
  const earning = cards.filter(
    (c) =>
      Number(c.real_cashback) !== 0 ||
      c.unexplained_count > 0 ||
      Number(c.eligible_spend) > 0,
  );
  if (earning.length === 0) return null;

  const max = Math.max(...earning.map((c) => Number(c.real_cashback)), 1);

  return (
    <Panel className="overflow-hidden">
      <div className="p-5 pb-3">
        <h2 className="font-display text-base font-medium text-ink">By card</h2>
        <p className="mt-1 text-sm text-muted">
          Effective rate is cashback divided by eligible spend — payments, fees and interest
          excluded. Click a row to narrow the page to that card.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[38rem] text-sm">
          <thead>
            <tr className="border-y border-hairline text-left">
              <th className="label-micro px-5 py-2 font-normal">Card</th>
              <th className="label-micro px-3 py-2 text-right font-normal">Cashback</th>
              <th className="label-micro px-3 py-2 text-right font-normal">Eligible spend</th>
              <th className="label-micro px-5 py-2 text-right font-normal">Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {earning.map((c) => (
              <tr
                key={c.card_id}
                onClick={() => onSelect(selectedId === c.card_id ? null : c.card_id)}
                className={cn(
                  "cursor-pointer transition-colors duration-300 hover:bg-raised/50",
                  selectedId === c.card_id && "bg-brass/[0.07] hover:bg-brass/[0.1]",
                  selectedId !== null && selectedId !== c.card_id && "opacity-55",
                )}
                aria-selected={selectedId === c.card_id}
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2 text-ink">
                    <CardSwatch theme={c.theme} />
                    {c.nickname || c.product_name || c.issuer}
                  </div>
                  {/* Why a card earns nothing, in the user's own words. Without
                      it a 0.00% reads as a verdict on the card rather than a
                      description of how it pays. */}
                  {c.rewards_note && Number(c.real_cashback) === 0 && (
                    <div className="mt-0.5 max-w-[22rem] text-xs text-faint">
                      {c.rewards_note}
                    </div>
                  )}
                  {/* A bar, not a chart library: the comparison is the point. */}
                  <div className="mt-1.5 h-1 w-32 overflow-hidden rounded-full bg-raised">
                    <div
                      className="h-full rounded-full bg-brass/70"
                      style={{
                        // Clamped: a card whose clawbacks exceeded its credits
                        // has a negative total, and a negative width silently
                        // renders as a full bar.
                        width: `${Math.max(0, (Number(c.real_cashback) / max) * 100)}%`,
                      }}
                    />
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right font-medium tabular-nums text-brass">
                  {formatMoney(c.real_cashback)}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-muted">
                  {formatMoney(c.eligible_spend)}
                </td>
                <td className="whitespace-nowrap px-5 py-3 text-right tabular-nums">
                  {c.effective_rate === null ? (
                    <span className="text-faint">—</span>
                  ) : c.unexplained_count > 0 && Number(c.real_cashback) === 0 ? (
                    <span
                      className="text-warn"
                      title={`This card has ${c.unexplained_count} credit(s) no rule explains, so its rate may be understated.`}
                    >
                      {c.effective_rate}%*
                    </span>
                  ) : (
                    <span className="text-ink">{c.effective_rate}%</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {earning.some((c) => c.unexplained_count > 0 && Number(c.real_cashback) === 0) && (
        <p className="border-t border-hairline px-5 py-3 text-xs text-faint">
          * This card has credits no rule explains, so its rate is a floor, not a
          measurement.
        </p>
      )}
    </Panel>
  );
}

function Entries({
  entries,
  fiscalYear,
  cardName,
  onAdd,
}: {
  entries: CashbackEntry[] | undefined;
  fiscalYear: string;
  cardName: string | null;
  onAdd: () => void;
}) {
  const qc = useQueryClient();

  const remove = useMutation({
    mutationFn: (id: string) => cashbackApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cashback"] });
      toast.success("Entry removed.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Panel className="overflow-hidden">
      <div className="flex items-start justify-between gap-3 p-5 pb-3">
        <div>
          <h2 className="font-display text-base font-medium text-ink">
            Entries · {formatFiscalYear(fiscalYear)}
            {cardName && <span className="text-muted"> · {cardName}</span>}
          </h2>
          <p className="mt-1 text-sm text-muted">
            Every credit behind the total, with the transaction it came from.
          </p>
        </div>
        <Button variant="default" size="sm" onClick={onAdd}>
          <Plus className="h-4 w-4" aria-hidden />
          Add realized reward
        </Button>
      </div>

      {!entries ? (
        <div className="space-y-2 p-5 pt-0">
          <div className="skeleton h-9 w-full" />
          <div className="skeleton h-9 w-full" />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={Coins}
          title={cardName ? "Nothing from this card this year" : "Nothing yet this year"}
          body="Cashback entries appear automatically when a statement credit matches one of your rules."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] text-sm">
            <thead>
              <tr className="border-y border-hairline text-left">
                <th className="label-micro px-5 py-2 font-normal">Date</th>
                <th className="label-micro px-3 py-2 font-normal">Description</th>
                <th className="label-micro px-3 py-2 font-normal">Type</th>
                <th className="label-micro px-5 py-2 text-right font-normal">Amount</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="whitespace-nowrap px-5 py-2.5 tabular-nums text-muted">
                    {formatDate(e.entry_date)}
                  </td>
                  <td className="px-3 py-2.5 text-ink">
                    {e.description}
                    {e.evidence_note && (
                      <span className="mt-0.5 block text-xs text-faint">{e.evidence_note}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {e.entry_type === "statement_credit" ? (
                      <Badge tone="brass">
                        <Sparkles className="h-2.5 w-2.5" aria-hidden />
                        Bill reduced
                      </Badge>
                    ) : e.entry_type === "statement_clawback" ? (
                      <Badge tone="warn">
                        <Undo2 className="h-2.5 w-2.5" aria-hidden />
                        Taken back
                      </Badge>
                    ) : (
                      <Badge tone="neutral">Realized</Badge>
                    )}
                  </td>
                  {/* A clawback is stored positive and subtracted, so the sign
                      is written in rather than implied by the stored figure —
                      an unsigned number in a column of credits reads as one. */}
                  <td
                    className={cn(
                      "whitespace-nowrap px-5 py-2.5 text-right font-medium tabular-nums",
                      e.entry_type === "statement_credit"
                        ? "text-brass"
                        : e.entry_type === "statement_clawback"
                          ? "text-warn"
                          : "text-ink",
                    )}
                  >
                    {e.entry_type === "statement_clawback" ? "−" : ""}
                    {formatMoney(e.amount_inr)}
                  </td>
                  <td className="px-2 py-2.5">
                    {/* Auto entries have no delete: they mirror a statement row,
                        and removing one here would put the ledger out of step
                        with the document it came from. */}
                    {e.created_by === "manual" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => remove.mutate(e.id)}
                        aria-label="Remove entry"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function ManualEntryDialog({
  fiscalYear,
  onClose,
}: {
  fiscalYear: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: cards } = useQuery({ queryKey: ["cards", false], queryFn: () => cardsApi.list(false) });

  const [form, setForm] = useState({
    card_id: "",
    amount_inr: "",
    entry_date: new Date().toISOString().slice(0, 10),
    description: "",
    evidence_note: "",
  });
  const [error, setError] = useState<string>();

  const create = useMutation({
    mutationFn: () => cashbackApi.addManual(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cashback"] });
      toast.success("Realized reward logged.");
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <Panel className="w-full max-w-lg p-5">
        <h2 className="font-display text-base font-medium text-ink">Add a realized reward</h2>
        <p className="mt-1 text-sm text-muted">
          Value you actually received that didn't reduce a bill — points redeemed for a
          voucher, a wallet credit. It's counted separately from Real Cashback, on purpose.
        </p>

        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(undefined);
            if (!form.card_id) return setError("Which card earned this?");
            if (!(Number(form.amount_inr) > 0)) return setError("Enter an amount above zero.");
            if (!form.description.trim()) return setError("Say what this was.");
            create.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Card">
              <select
                value={form.card_id}
                onChange={(e) => setForm((f) => ({ ...f, card_id: e.target.value }))}
                className={cn(SELECT_CLASS, "w-full")}
              >
                <option value="">Choose…</option>
                {cards?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nickname || c.product_name || c.issuer}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Amount" hint="₹">
              <Input
                inputMode="decimal"
                value={form.amount_inr}
                onChange={(e) => setForm((f) => ({ ...f, amount_inr: e.target.value }))}
                placeholder="500.00"
              />
            </Field>
          </div>

          <Field label="Date" hint={`Filed under ${formatFiscalYear(fiscalYear)} if it falls in that year.`}>
            <Input
              type="date"
              value={form.entry_date}
              onChange={(e) => setForm((f) => ({ ...f, entry_date: e.target.value }))}
            />
          </Field>

          <Field label="What was it?">
            <Input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="12,000 points redeemed for an Amazon voucher"
            />
          </Field>

          <Field label="Evidence" hint="Where the value landed, for your own audit trail.">
            <Input
              value={form.evidence_note}
              onChange={(e) => setForm((f) => ({ ...f, evidence_note: e.target.value }))}
              placeholder="Voucher code ending 4821, redeemed 12 Jul"
            />
          </Field>

          {error && <p className="text-xs text-overdue">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Add entry"}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  );
}
